import React, { useEffect, useMemo, useState } from 'react';
import { plannerAPI, studentsAPI } from '../services/api';
import { useYear } from '../context/YearContext';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const EVENT_TYPES = ['general', 'holiday', 'exam', 'meeting', 'activity'];
const inputStyle = {
  padding: '8px 10px',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 14,
};

export default function TimetableEvents() {
  const { year } = useYear();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [timetable, setTimetable] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingTimetable, setSavingTimetable] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('timetable');

  const [timetableForm, setTimetableForm] = useState({
    class: '',
    day_name: 'Monday',
    period_name: '',
    subject_name: '',
    teacher_name: '',
    start_time: '',
    end_time: '',
    room_no: '',
  });

  const [eventForm, setEventForm] = useState({
    event_title: '',
    event_type: 'general',
    event_date: '',
    class_scope: 'All Classes',
    description: '',
  });

  useEffect(() => {
    if (!year) return;
    studentsAPI.getClasses(year)
      .then((res) => {
        const list = res.data.data || [];
        setClasses(list);
        if (list[0]) {
          setSelectedClass(list[0]);
          setTimetableForm((prev) => ({ ...prev, class: list[0] }));
        } else {
          loadData('');
        }
      })
      .catch(() => loadData(''));
  }, [year]);

  const loadData = (cls = selectedClass) => {
    setLoading(true);
    Promise.all([
      plannerAPI.getTimetable({ year, class: cls || undefined }),
      plannerAPI.getEvents({ year }),
    ])
      .then(([ttRes, evRes]) => {
        setTimetable(ttRes.data.data || []);
        setEvents(evRes.data.data || []);
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load planner data'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData(selectedClass);
  }, [selectedClass, year]); // eslint-disable-line react-hooks/exhaustive-deps

  const groupedTimetable = useMemo(() => {
    const map = {};
    DAYS.forEach((day) => { map[day] = []; });
    timetable.forEach((row) => {
      if (!map[row.day_name]) map[row.day_name] = [];
      map[row.day_name].push(row);
    });
    return map;
  }, [timetable]);

  const handleTimetableSave = async (e) => {
    e.preventDefault();
    setSavingTimetable(true);
    setError('');
    try {
      await plannerAPI.createTimetable({ ...timetableForm, academic_year: year });
      setTimetableForm((prev) => ({
        ...prev,
        period_name: '',
        subject_name: '',
        teacher_name: '',
        start_time: '',
        end_time: '',
        room_no: '',
      }));
      loadData(selectedClass);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save timetable entry');
    } finally {
      setSavingTimetable(false);
    }
  };

  const handleEventSave = async (e) => {
    e.preventDefault();
    setSavingEvent(true);
    setError('');
    try {
      await plannerAPI.createEvent({ ...eventForm, academic_year: year });
      setEventForm({
        event_title: '',
        event_type: 'general',
        event_date: '',
        class_scope: 'All Classes',
        description: '',
      });
      loadData(selectedClass);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save event');
    } finally {
      setSavingEvent(false);
    }
  };

  const deleteTimetable = async (row) => {
    if (!window.confirm(`Delete ${row.day_name} ${row.period_name}?`)) return;
    await plannerAPI.deleteTimetable(row.timetable_id);
    loadData(selectedClass);
  };

  const deleteEvent = async (event) => {
    if (!window.confirm(`Delete event "${event.event_title}"?`)) return;
    await plannerAPI.deleteEvent(event.event_id);
    loadData(selectedClass);
  };

  return (
    <div className="page-content">
      <div className="page-header" style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Timetable & Events</h2>
        <p style={{ margin: '6px 0 0', color: '#64748b' }}>
          Manage class schedules and maintain a simple school event calendar.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <button className="btn btn-secondary" onClick={() => setTab('timetable')} style={{ background: tab === 'timetable' ? '#dbeafe' : undefined }}>
          Timetable
        </button>
        <button className="btn btn-secondary" onClick={() => setTab('events')} style={{ background: tab === 'events' ? '#dbeafe' : undefined }}>
          Events
        </button>
      </div>

      {error && <div className="error-banner" style={{ marginBottom: 12 }}>{error}</div>}

      {tab === 'timetable' ? (
        <>
          <form onSubmit={handleTimetableSave} className="card" style={{ padding: 18, marginBottom: 18 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
              <select
                value={timetableForm.class}
                onChange={(e) => {
                  setTimetableForm((prev) => ({ ...prev, class: e.target.value }));
                  setSelectedClass(e.target.value);
                }}
                style={inputStyle}
                required
              >
                <option value="">Select class</option>
                {classes.map((cls) => <option key={cls} value={cls}>{cls}</option>)}
              </select>
              <select value={timetableForm.day_name} onChange={(e) => setTimetableForm((prev) => ({ ...prev, day_name: e.target.value }))} style={inputStyle}>
                {DAYS.map((day) => <option key={day} value={day}>{day}</option>)}
              </select>
              <input value={timetableForm.period_name} onChange={(e) => setTimetableForm((prev) => ({ ...prev, period_name: e.target.value }))} placeholder="Period" style={inputStyle} required />
              <input value={timetableForm.subject_name} onChange={(e) => setTimetableForm((prev) => ({ ...prev, subject_name: e.target.value }))} placeholder="Subject" style={inputStyle} required />
              <input value={timetableForm.teacher_name} onChange={(e) => setTimetableForm((prev) => ({ ...prev, teacher_name: e.target.value }))} placeholder="Teacher" style={inputStyle} />
              <input type="time" value={timetableForm.start_time} onChange={(e) => setTimetableForm((prev) => ({ ...prev, start_time: e.target.value }))} style={inputStyle} />
              <input type="time" value={timetableForm.end_time} onChange={(e) => setTimetableForm((prev) => ({ ...prev, end_time: e.target.value }))} style={inputStyle} />
              <input value={timetableForm.room_no} onChange={(e) => setTimetableForm((prev) => ({ ...prev, room_no: e.target.value }))} placeholder="Room" style={inputStyle} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, gap: 12, flexWrap: 'wrap' }}>
              <span style={{ color: '#64748b', fontSize: 13 }}>Academic year: {year}</span>
              <button className="btn btn-primary" type="submit" disabled={savingTimetable}>
                {savingTimetable ? 'Saving...' : 'Add Timetable Entry'}
              </button>
            </div>
          </form>

          {loading ? (
            <div className="loading">Loading timetable...</div>
          ) : (
            DAYS.map((day) => (
              <div key={day} className="card" style={{ padding: 18, marginBottom: 14 }}>
                <h3 style={{ marginTop: 0 }}>{day}</h3>
                {groupedTimetable[day]?.length ? (
                  <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Period</th>
                        <th>Time</th>
                        <th>Subject</th>
                        <th>Teacher</th>
                        <th>Room</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedTimetable[day].map((row) => (
                        <tr key={row.timetable_id}>
                          <td>{row.period_name}</td>
                          <td>{row.start_time || '-'} {row.end_time ? `- ${row.end_time}` : ''}</td>
                          <td>{row.subject_name}</td>
                          <td>{row.teacher_name || '-'}</td>
                          <td>{row.room_no || '-'}</td>
                          <td><button className="btn btn-sm btn-danger" onClick={() => deleteTimetable(row)}>Delete</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                ) : (
                  <p style={{ color: '#94a3b8', marginBottom: 0 }}>No periods added for {day}.</p>
                )}
              </div>
            ))
          )}
        </>
      ) : (
        <>
          <form onSubmit={handleEventSave} className="card" style={{ padding: 18, marginBottom: 18 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <input value={eventForm.event_title} onChange={(e) => setEventForm((prev) => ({ ...prev, event_title: e.target.value }))} placeholder="Event title" style={inputStyle} required />
              <select value={eventForm.event_type} onChange={(e) => setEventForm((prev) => ({ ...prev, event_type: e.target.value }))} style={inputStyle}>
                {EVENT_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
              <input type="date" value={eventForm.event_date} onChange={(e) => setEventForm((prev) => ({ ...prev, event_date: e.target.value }))} style={inputStyle} required lang="en-GB" />
              <select value={eventForm.class_scope} onChange={(e) => setEventForm((prev) => ({ ...prev, class_scope: e.target.value }))} style={inputStyle}>
                <option value="All Classes">All Classes</option>
                {classes.map((cls) => <option key={cls} value={cls}>{cls}</option>)}
              </select>
            </div>
            <textarea
              value={eventForm.description}
              onChange={(e) => setEventForm((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Event description"
              rows={4}
              style={{ ...inputStyle, width: '100%', marginTop: 12, resize: 'vertical' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, gap: 12, flexWrap: 'wrap' }}>
              <span style={{ color: '#64748b', fontSize: 13 }}>Academic year: {year}</span>
              <button className="btn btn-primary" type="submit" disabled={savingEvent}>
                {savingEvent ? 'Saving...' : 'Add Event'}
              </button>
            </div>
          </form>

          {loading ? (
            <div className="loading">Loading events...</div>
          ) : events.length === 0 ? (
            <div className="empty-state"><p>No events added for {year}.</p></div>
          ) : (
            <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Class Scope</th>
                  <th>Description</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.event_id}>
                    <td>{event.event_date}</td>
                    <td style={{ fontWeight: 600 }}>{event.event_title}</td>
                    <td style={{ textTransform: 'capitalize' }}>{event.event_type}</td>
                    <td>{event.class_scope}</td>
                    <td style={{ maxWidth: 340, color: '#475569' }}>{event.description || '-'}</td>
                    <td><button className="btn btn-sm btn-danger" onClick={() => deleteEvent(event)}>Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
